/**
 * AI Module - Fact Pack Builder
 *
 * Transforms Zustand store state into the FactPack schema for the LLM.
 * This is the bridge between raw app state and the AI-ready input format.
 *
 * Phase 3 Update: Now includes pre-computed journey data from Rule Engine.
 * LLM must use these exact values - no recalculation allowed.
 */

import type { CalculatorState, FIREResults, CountryCode } from '@/types';
import type { FactPack, ConfidenceLevel, PriorityAction, PreComputedJourneyData } from '../schemas/factPack.schema';
import { quickRNOREstimate } from '@/lib/calculators/rnorCalculator';
import { computeJourneyData, type ComputedJourneyData } from '@/lib/calculators/journeyCalculator';
import { getEffectiveUSAAssets } from '@/lib/utils/assetHelpers';

/**
 * Transform ComputedJourneyData from Rule Engine to FactPack schema format.
 * This ensures the data structure matches the Zod schema for validation.
 */
function transformToPreComputedJourneyData(computed: ComputedJourneyData): PreComputedJourneyData {
  return {
    computedAt: computed.computedAt,
    dataVersion: computed.dataVersion,
    dates: computed.dates,
    taxSavings: computed.taxSavings,
    socialSecurity: computed.socialSecurity,
    scenarios: computed.scenarios,
    liquidationSequence: computed.liquidationSequence,
    milestones: computed.milestones,
    riskFlags: computed.riskFlags,
    insightSeeds: computed.insightSeeds,
    heroStats: computed.heroStats,
    confidenceScore: computed.confidenceScore,
  };
}

// Helper to map country code to FactPack country
function mapCountry(code: CountryCode): 'USA' | 'UAE' | 'UK' | 'Canada' | 'Singapore' {
  const mapping: Record<string, 'USA' | 'UAE' | 'UK' | 'Canada' | 'Singapore'> = {
    USA: 'USA',
    UAE: 'UAE',
    UK: 'UK',
    Canada: 'Canada',
    Singapore: 'Singapore',
  };
  return mapping[code] || 'USA';
}

// Helper to determine FIRE status
function determineFIREStatus(
  progressPercent: number,
  yearsToFIRE: number
): 'achieved' | 'on_track' | 'at_risk' | 'behind' {
  if (progressPercent >= 100) return 'achieved';
  if (progressPercent >= 70 && yearsToFIRE <= 10) return 'on_track';
  if (progressPercent >= 40) return 'at_risk';
  return 'behind';
}

// Helper to determine FIRE type based on annual expenses
function determineFIREType(
  annualExpenses: number
): 'lean' | 'regular' | 'fat' {
  if (annualExpenses < 600000) return 'lean';
  if (annualExpenses < 1500000) return 'regular';
  return 'fat';
}

// Helper to calculate confidence level
function calculateConfidence(
  missingFieldsCount: number,
  hasRetirementAccounts: boolean,
  hasFireResults: boolean
): ConfidenceLevel {
  if (!hasFireResults || missingFieldsCount > 5) return 'low';
  if (missingFieldsCount > 2 || !hasRetirementAccounts) return 'medium';
  return 'high';
}

// Helper to determine RNOR impact level based on source country
function getRNORImpactLevel(
  country: CountryCode
): 'high' | 'medium' | 'low' | 'none' {
  // UAE has no income tax, so RNOR has low impact (no foreign income tax to optimize)
  if (country === 'UAE') return 'low';
  // USA, UK, Canada have significant income taxes - high impact
  if (country === 'USA' || country === 'UK' || country === 'Canada') return 'high';
  // Singapore has moderate taxes
  return 'medium';
}

// Helper to calculate RNOR dates
function calculateRNORDates(
  returnYear: number,
  returnMonth: number = 4 // Default April for FY start
): { startDate: string; endDate: string; years: number } {
  // RNOR typically lasts 2-3 years after return
  // Simplification: assume 2 full years from FY after return
  const fyStartYear = returnMonth >= 4 ? returnYear : returnYear - 1;
  const rnorEndYear = fyStartYear + 3; // 2-3 years RNOR period

  return {
    startDate: `April ${fyStartYear + 1}`,
    endDate: `March ${rnorEndYear}`,
    years: 2, // Conservative estimate
  };
}

// Helper to calculate urgency from days remaining
function calculateUrgency(daysRemaining: number | undefined): 'critical' | 'high' | 'medium' | 'low' {
  if (daysRemaining === undefined) return 'low';
  if (daysRemaining <= 14) return 'critical';
  if (daysRemaining <= 30) return 'high';
  if (daysRemaining <= 90) return 'medium';
  return 'low';
}

// Helper to format date
function formatDateReadable(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// Helper to calculate days from now
function daysFromNow(targetDate: Date): number {
  const now = new Date();
  return Math.max(0, Math.floor((targetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
}

// Helper to generate priority actions from state with enhanced deadline fields
function generatePriorityActions(
  state: CalculatorState,
  country: CountryCode
): PriorityAction[] {
  const actions: PriorityAction[] = [];
  let actionId = 1;

  const currentYear = new Date().getFullYear();
  const returnYear = state.profile.expectedReturnYear || currentYear + 2;
  const returnMonth = 4; // April
  const targetDate = new Date(returnYear, returnMonth - 1, 1);

  // Key milestone dates
  const sixMonthsBefore = new Date(targetDate);
  sixMonthsBefore.setMonth(sixMonthsBefore.getMonth() - 6);
  const threeMonthsBefore = new Date(targetDate);
  threeMonthsBefore.setMonth(threeMonthsBefore.getMonth() - 3);
  const finalTaxReturnDate = new Date(returnYear + 1, 3, 15); // April 15 of year after departure

  // ==========================================
  // USA-specific actions
  // ==========================================
  if (country === 'USA') {
    // Get effective USA assets from new architecture (with fallback to legacy)
    const usaAssets = getEffectiveUSAAssets(state);

    // 401(k) maximization
    actions.push({
      id: `action_${actionId++}`,
      category: 'tax_optimization',
      title: 'Maximize 401(k) contributions before return',
      description: 'Continue maxing out 401(k) contributions ($23,500 for 2025) to defer taxes while in USA tax system.',
      timing: 'before_return',
      deadlineDate: threeMonthsBefore.toISOString().split('T')[0],
      deadlineFormatted: formatDateReadable(threeMonthsBefore),
      daysRemaining: daysFromNow(threeMonthsBefore),
      urgency: calculateUrgency(daysFromNow(threeMonthsBefore)),
      completed: false,
      impact: 'high',
      estimatedBenefit: 'Tax deferral on $23.5K annually',
      requiresCAReview: false,
    });

    // HSA - Profile triggered (if HSA balance > $1000)
    if (usaAssets.balanceHSA > 1000) {
      const hsaBalance = usaAssets.balanceHSA;
      actions.push({
        id: `action_${actionId++}`,
        category: 'tax_optimization',
        title: 'Use HSA for medical expenses',
        description: `Spend $${hsaBalance.toLocaleString()} HSA balance on qualified medical expenses before losing US residency. HSA cannot easily be used for India medical expenses.`,
        timing: 'before_return',
        deadlineDate: sixMonthsBefore.toISOString().split('T')[0],
        deadlineFormatted: formatDateReadable(sixMonthsBefore),
        daysRemaining: daysFromNow(sixMonthsBefore),
        urgency: calculateUrgency(daysFromNow(sixMonthsBefore)),
        completed: false,
        impact: 'medium',
        estimatedBenefit: `Tax-free medical spending: $${hsaBalance.toLocaleString()}`,
        requiresCAReview: false,
      });
    }

    // RSUs - Profile triggered (if RSU grants exist OR simple rsuEquityValue entered)
    const rsuGrantsValue = usaAssets.rsuGrants.reduce((sum, g) => sum + g.vestedShares * g.currentPricePerShare, 0);
    const rsuValue = rsuGrantsValue + (usaAssets.rsuEquityValue || 0);
    if (rsuValue > 10000) {
      actions.push({
        id: `action_${actionId++}`,
        category: 'tax_optimization',
        title: 'Review RSU vesting schedule',
        description: `$${Math.round(rsuValue).toLocaleString()} in vested RSUs. Sell before move to lock in US LTCG rates (15%) vs India STCG (20%+).`,
        timing: 'before_return',
        deadlineDate: threeMonthsBefore.toISOString().split('T')[0],
        deadlineFormatted: formatDateReadable(threeMonthsBefore),
        daysRemaining: daysFromNow(threeMonthsBefore),
        urgency: calculateUrgency(daysFromNow(threeMonthsBefore)),
        completed: false,
        impact: rsuValue > 100000 ? 'critical' : 'high',
        estimatedBenefit: `Potential tax savings: ~5% on $${Math.round(rsuValue).toLocaleString()}`,
        requiresCAReview: rsuValue > 100000,
        caReviewReason: rsuValue > 100000 ? 'Large RSU liquidation requires tax planning' : undefined,
      });
    }

    // ESPP - Profile triggered
    if (usaAssets.esppBalance > 5000) {
      actions.push({
        id: `action_${actionId++}`,
        category: 'tax_optimization',
        title: 'ESPP sale timing decision',
        description: `$${usaAssets.esppBalance.toLocaleString()} in ESPP shares. Sell before move to lock in US tax treatment on discount portion.`,
        timing: 'before_return',
        deadlineDate: threeMonthsBefore.toISOString().split('T')[0],
        deadlineFormatted: formatDateReadable(threeMonthsBefore),
        daysRemaining: daysFromNow(threeMonthsBefore),
        urgency: calculateUrgency(daysFromNow(threeMonthsBefore)),
        completed: false,
        impact: 'medium',
        requiresCAReview: usaAssets.esppBalance > 50000,
      });
    }

    // Taxable brokerage - Profile triggered
    if (usaAssets.taxableBrokerage > 0) {
      actions.push({
        id: `action_${actionId++}`,
        category: 'tax_optimization',
        title: 'Tax-loss harvest brokerage positions',
        description: 'Review brokerage account for tax-loss harvesting opportunities before changing tax residency.',
        timing: 'before_return',
        deadlineDate: threeMonthsBefore.toISOString().split('T')[0],
        deadlineFormatted: formatDateReadable(threeMonthsBefore),
        daysRemaining: daysFromNow(threeMonthsBefore),
        urgency: calculateUrgency(daysFromNow(threeMonthsBefore)),
        completed: false,
        impact: 'medium',
        requiresCAReview: true,
        caReviewReason: 'Optimize capital gains strategy across jurisdictions',
      });
    }

    // Social Security credits check - Profile triggered (if close to 40 credits)
    const yearsWorked = state.profile.yearsWorkedInUSA || 0;
    if (yearsWorked >= 8 && yearsWorked < 10) {
      const yearsNeeded = 10 - yearsWorked;
      actions.push({
        id: `action_${actionId++}`,
        category: 'timing',
        title: 'Social Security credits check',
        description: `You have ~${yearsWorked * 4} of 40 SS credits. ${yearsNeeded} more year(s) of work could qualify you for lifetime SS benefits (~$200K+ value).`,
        timing: 'before_return',
        completed: false,
        impact: 'high',
        estimatedBenefit: 'Potential $200K+ lifetime Social Security benefits',
        requiresCAReview: false,
      });
    }

    // Final US tax return (critical compliance)
    actions.push({
      id: `action_${actionId++}`,
      category: 'compliance',
      title: 'File final US tax return and FBAR',
      description: 'Ensure final US tax return is filed and FBAR obligations are met for the departure year.',
      timing: 'before_return',
      deadline: 'April 15 of year after departure',
      deadlineDate: finalTaxReturnDate.toISOString().split('T')[0],
      deadlineFormatted: formatDateReadable(finalTaxReturnDate),
      daysRemaining: daysFromNow(finalTaxReturnDate),
      urgency: calculateUrgency(daysFromNow(finalTaxReturnDate)),
      completed: false,
      impact: 'critical',
      requiresCAReview: true,
      caReviewReason: 'Cross-border tax compliance is complex',
    });

    // Form 67 for DTAA credit
    const form67Deadline = new Date(returnYear + 1, 6, 31); // July 31 of year after return
    actions.push({
      id: `action_${actionId++}`,
      category: 'compliance',
      title: 'File Form 67 for DTAA credit',
      description: 'Submit Form 67 to claim foreign tax credit under India-USA DTAA. Required to avoid double taxation.',
      timing: 'before_return',
      deadlineDate: form67Deadline.toISOString().split('T')[0],
      deadlineFormatted: formatDateReadable(form67Deadline),
      daysRemaining: daysFromNow(form67Deadline),
      urgency: calculateUrgency(daysFromNow(form67Deadline)),
      completed: false,
      impact: 'critical',
      requiresCAReview: true,
      caReviewReason: 'DTAA credit requires proper documentation',
    });
  }

  // ==========================================
  // UAE-specific actions
  // ==========================================
  if (country === 'UAE') {
    actions.push({
      id: `action_${actionId++}`,
      category: 'timing',
      title: 'Time return to optimize FY residency',
      description: 'Since UAE has no income tax, focus on timing return to India to maximize RNOR benefits for any India-sourced income.',
      timing: 'before_return',
      deadlineDate: threeMonthsBefore.toISOString().split('T')[0],
      deadlineFormatted: formatDateReadable(threeMonthsBefore),
      daysRemaining: daysFromNow(threeMonthsBefore),
      urgency: calculateUrgency(daysFromNow(threeMonthsBefore)),
      completed: false,
      impact: 'medium',
      requiresCAReview: false,
    });
  }

  // ==========================================
  // Universal actions (all countries)
  // ==========================================

  // Children - Profile triggered (school records apostille)
  if (state.profile.children.length > 0) {
    const schoolAgeChildren = state.profile.children.filter(c => c.age >= 3 && c.age <= 18);
    if (schoolAgeChildren.length > 0) {
      actions.push({
        id: `action_${actionId++}`,
        category: 'compliance',
        title: 'School records apostille',
        description: `Get apostille on school records for ${schoolAgeChildren.length} school-age child(ren). Required for admission to Indian schools.`,
        timing: 'before_return',
        deadlineDate: threeMonthsBefore.toISOString().split('T')[0],
        deadlineFormatted: formatDateReadable(threeMonthsBefore),
        daysRemaining: daysFromNow(threeMonthsBefore),
        urgency: calculateUrgency(daysFromNow(threeMonthsBefore)),
        completed: false,
        impact: 'high',
        requiresCAReview: false,
      });
    }
  }

  // Account setup
  actions.push({
    id: `action_${actionId++}`,
    category: 'account_setup',
    title: 'Open resident savings account in India',
    description: 'Convert NRE/NRO accounts to resident accounts or open new resident savings accounts upon return.',
    timing: 'during_rnor',
    completed: false,
    impact: 'high',
    requiresCAReview: false,
  });

  // Insurance actions
  if (state.safetyNet.healthInsuranceCoverINR < 1000000) {
    actions.push({
      id: `action_${actionId++}`,
      category: 'insurance',
      title: 'Secure comprehensive health insurance',
      description: 'Obtain health insurance with minimum ₹10L coverage before returning. Pre-existing conditions are easier to cover while employed abroad.',
      timing: 'before_return',
      deadlineDate: sixMonthsBefore.toISOString().split('T')[0],
      deadlineFormatted: formatDateReadable(sixMonthsBefore),
      daysRemaining: daysFromNow(sixMonthsBefore),
      urgency: calculateUrgency(daysFromNow(sixMonthsBefore)),
      completed: false,
      impact: 'critical',
      requiresCAReview: false,
    });
  }

  const fireNumber = state.fireResults?.fireNumber ?? 0;
  if (state.safetyNet.termInsuranceCoverINR < fireNumber) {
    actions.push({
      id: `action_${actionId++}`,
      category: 'insurance',
      title: 'Review term insurance coverage',
      description: 'Ensure term insurance coverage is adequate for family protection. Consider policies that work across borders.',
      timing: 'before_return',
      deadlineDate: sixMonthsBefore.toISOString().split('T')[0],
      deadlineFormatted: formatDateReadable(sixMonthsBefore),
      daysRemaining: daysFromNow(sixMonthsBefore),
      urgency: calculateUrgency(daysFromNow(sixMonthsBefore)),
      completed: false,
      impact: 'high',
      requiresCAReview: false,
    });
  }

  // Sort by urgency (critical first, then high, medium, low)
  const urgencyOrder = { critical: 0, high: 1, medium: 2, low: 3 };
  actions.sort((a, b) => {
    const aUrgency = a.urgency || 'low';
    const bUrgency = b.urgency || 'low';
    return urgencyOrder[aUrgency] - urgencyOrder[bUrgency];
  });

  return actions;
}

// Helper to generate residential status timeline
function generateResidentialTimeline(
  returnYear: number
): Array<{
  fiscalYear: string;
  status: 'NRI' | 'RNOR' | 'ROR';
  foreignIncomeStatus: 'not_taxable' | 'taxable';
}> {
  const timeline = [];

  // Year before return - NRI
  timeline.push({
    fiscalYear: `FY ${returnYear - 1}-${String(returnYear).slice(2)}`,
    status: 'NRI' as const,
    foreignIncomeStatus: 'not_taxable' as const,
  });

  // Return year - likely RNOR (depends on exact timing)
  timeline.push({
    fiscalYear: `FY ${returnYear}-${String(returnYear + 1).slice(2)}`,
    status: 'RNOR' as const,
    foreignIncomeStatus: 'not_taxable' as const,
  });

  // Year after return - RNOR continues
  timeline.push({
    fiscalYear: `FY ${returnYear + 1}-${String(returnYear + 2).slice(2)}`,
    status: 'RNOR' as const,
    foreignIncomeStatus: 'not_taxable' as const,
  });

  // Following years - ROR
  timeline.push({
    fiscalYear: `FY ${returnYear + 2}-${String(returnYear + 3).slice(2)}`,
    status: 'ROR' as const,
    foreignIncomeStatus: 'taxable' as const,
  });

  return timeline;
}

/**
 * Build a FactPack from the current app state
 */
export function buildFactPack(state: CalculatorState): FactPack {
  const {
    profile,
    location,
    indiaAssets,
    residentCountryAssets,
    assumptions,
    fireResults,
  } = state;

  // Use helper to get effective USA assets from new architecture (with fallback)
  const usaAssets = getEffectiveUSAAssets(state);

  const country = mapCountry(profile.residentCountry);
  const currentYear = new Date().getFullYear();
  const returnYear = profile.expectedReturnYear || currentYear + 2;

  // Detect if user has already returned to India (return year is in the past or current year)
  const hasAlreadyReturned = returnYear <= currentYear;
  const returnStatus: 'planning' | 'returned' = hasAlreadyReturned ? 'returned' : 'planning';

  // Use RNOR result from state (already calculated in TaxTab)
  // Fallback: recalculate if not available (e.g., user hasn't visited Tax tab)
  let rnorCalculation = state.rnorResult;
  if (!rnorCalculation) {
    const nriSinceYear = profile.nriSinceYear || returnYear - 3;
    rnorCalculation = quickRNOREstimate(nriSinceYear, returnYear, 45);
  }

  const rnorDates = calculateRNORDates(returnYear);

  // Track missing fields for data quality
  const missingFields: Array<{
    field: string;
    impact: 'blocks_calculation' | 'reduces_accuracy' | 'nice_to_have';
    assumption?: string;
  }> = [];

  if (!profile.expectedReturnYear) {
    missingFields.push({
      field: 'expectedReturnYear',
      impact: 'reduces_accuracy',
      assumption: `Assumed return in ${returnYear}`,
    });
  }

  if (!profile.nriSinceYear) {
    const nriSinceYear = profile.nriSinceYear || returnYear - 3;
    missingFields.push({
      field: 'nriSinceYear',
      impact: 'reduces_accuracy',
      assumption: `Assumed NRI since ${nriSinceYear} (3 years ago) for RNOR calculation`,
    });
  }

  if (!state.rnorResult) {
    missingFields.push({
      field: 'rnorResult',
      impact: 'reduces_accuracy',
      assumption: 'RNOR eligibility recalculated. May differ from displayed calculation if inputs changed.',
    });
  }

  if (!fireResults) {
    missingFields.push({
      field: 'fireResults',
      impact: 'blocks_calculation',
    });
  }

  // Calculate RSU value: detailed grants + simple rsuEquityValue (for users who don't enter detailed grants)
  const rsuGrantsValue = usaAssets.rsuGrants.reduce(
    (sum, grant) => sum + grant.vestedShares * grant.currentPricePerShare,
    0
  );
  const rsuValue = rsuGrantsValue + (usaAssets.rsuEquityValue || 0);

  // Calculate stock options intrinsic value (only if in-the-money)
  const stockOptionsValue = (usaAssets.stockOptions || []).reduce((sum, option) => {
    const intrinsicValue = Math.max(0, option.currentPrice - option.strikePrice) * option.vestedShares;
    return sum + intrinsicValue;
  }, 0);

  // Calculate totals (now includes RSUs and stock options)
  const usaTotalAssets =
    usaAssets.balance401k +
    usaAssets.balanceRothIRA +
    usaAssets.balanceTraditionalIRA +
    usaAssets.balanceHSA +
    usaAssets.taxableBrokerage +
    usaAssets.savingsChecking +
    usaAssets.esppBalance +
    usaAssets.crypto +
    rsuValue +
    stockOptionsValue +
    (usaAssets.primaryHomeValue - usaAssets.primaryHomeMortgage) +
    (usaAssets.rentalPropertyValue - usaAssets.rentalPropertyMortgage);

  const indiaTotal =
    indiaAssets.npsBalance +
    indiaAssets.epfBalance +
    indiaAssets.ppfBalance +
    indiaAssets.mutualFunds +
    indiaAssets.stocks +
    indiaAssets.fixedDeposits +
    indiaAssets.gold +
    indiaAssets.realEstateValue +
    indiaAssets.nreAccountBalance +
    indiaAssets.nroAccountBalance;

  // Priority actions
  const priorityActions = generatePriorityActions(state, profile.residentCountry);

  // Calculate confidence
  const hasRetirement = usaAssets.balance401k > 0 || indiaAssets.npsBalance > 0;
  const confidence = calculateConfidence(
    missingFields.length,
    hasRetirement,
    !!fireResults
  );

  // Compute pre-calculated journey data from Rule Engine
  // This is the source of truth for all numbers - LLM will only narrate these values
  const computedJourney = computeJourneyData(state);
  const preComputedJourneyData = transformToPreComputedJourneyData(computedJourney);

  const factPack: FactPack = {
    // Meta
    generatedAt: new Date().toISOString(),
    engineVersion: '2.1.0',

    // Profile
    profile: {
      firstName: profile.firstName, // For personalized headlines
      age: profile.currentAge,
      retirementAge: profile.retirementAge,
      lifeExpectancy: profile.lifeExpectancy,
      currentCountry: country,
      targetCountry: 'India',
      targetReturnYear: returnYear,
      returnStatus, // 'planning' if future return, 'returned' if already in India
      maritalStatus: profile.maritalStatus === 'married' ? 'married' : 'single',
      spouseAge: profile.spouseAge,
      hasChildren: profile.children.length > 0,
      numberOfChildren: profile.children.length,
      childrenAges: profile.children.map((c) => c.age),
    },

    // FIRE Snapshot
    fireSnapshot: {
      fireNumber: fireResults?.fireNumber || 0,
      fireNumberUSD: fireResults?.fireNumberUSD || 0,
      currentNetWorth: fireResults?.currentNetWorth || 0,
      currentNetWorthUSD: fireResults?.currentNetWorthUSD || 0,
      progressPercent: fireResults?.progressPercent || 0,
      yearsToFIRE: fireResults?.yearsToFIRE || 0,
      monthsToFIRE: (fireResults?.yearsToFIRE || 0) * 12,
      fireStatus: determineFIREStatus(
        fireResults?.progressPercent || 0,
        fireResults?.yearsToFIRE || 0
      ),
      fireType: determineFIREType(fireResults?.annualExpensesAtFIRE || 1000000),
      monthlyExpensesAtFIRE_INR: fireResults?.monthlyExpensesAtFIRE || 0,
      annualExpensesAtFIRE_INR: fireResults?.annualExpensesAtFIRE || 0,
      safeWithdrawalRate: assumptions.safeWithdrawalRate / 100,
      confidence,
      confidenceFactors:
        confidence === 'high'
          ? ['Complete financial data', 'FIRE calculation completed']
          : confidence === 'medium'
          ? ['Some data missing', 'Estimates used for projections']
          : ['Significant data gaps', 'Results are preliminary'],
    },

    // Assets
    assets: {
      residentCountry: {
        total: country === 'USA' ? usaTotalAssets : Object.values(residentCountryAssets.assets).reduce((a, b) => a + (b || 0), 0),
        currency: country === 'USA' ? 'USD' : country === 'UAE' ? 'AED' : 'USD',
        breakdown: {
          retirement:
            usaAssets.balance401k +
            usaAssets.balanceRothIRA +
            usaAssets.balanceTraditionalIRA +
            usaAssets.balanceHSA,
          taxableInvestments: usaAssets.taxableBrokerage + usaAssets.crypto,
          rsusOptions: usaAssets.esppBalance + rsuValue + stockOptionsValue,
          liquid: usaAssets.savingsChecking,
          realEstate:
            (usaAssets.primaryHomeValue - usaAssets.primaryHomeMortgage) +
            (usaAssets.rentalPropertyValue - usaAssets.rentalPropertyMortgage),
        },
      },
      india: {
        total: indiaTotal,
        currency: 'INR',
        breakdown: {
          nps: indiaAssets.npsBalance,
          epfPpf: indiaAssets.epfBalance + indiaAssets.ppfBalance,
          mutualFunds: indiaAssets.mutualFunds,
          stocks: indiaAssets.stocks,
          realEstate: indiaAssets.realEstateValue,
          nreNro: indiaAssets.nreAccountBalance + indiaAssets.nroAccountBalance,
          fd: indiaAssets.fixedDeposits,
        },
      },
    },

    // Tax Strategy
    taxStrategy: {
      rnpioBased: {
        eligible: rnorCalculation.eligible,
        eligibilityReason: rnorCalculation.eligible
          ? rnorCalculation.explanation
          : `Not eligible for RNOR. ${rnorCalculation.explanation}`,
        rnorStartDate: rnorCalculation.eligible ? rnorDates.startDate : '',
        rnorEndDate: rnorCalculation.eligible ? rnorDates.endDate : '',
        rnorYears: rnorCalculation.eligible ? rnorCalculation.estimatedYears : 0,
        impactLevel: rnorCalculation.eligible
          ? getRNORImpactLevel(profile.residentCountry)
          : 'none',
      },
      residentialStatusTimeline: generateResidentialTimeline(returnYear),
      // Liquidation plan comes from Rule Engine (preComputedJourneyData.liquidationSequence)
      // Map to the FactPack schema format
      liquidationPlan: computedJourney.liquidationSequence.map(item => ({
        asset: item.asset,
        recommendedAction: item.action,
        recommendedTiming: item.timingDescription,
        taxImplication: item.taxImplication,
        estimatedTaxSavings_INR: 0, // Tax savings tracked separately
        requiresCAReview: item.requiresCAReview,
        caReviewReason: item.requiresCAReview ? 'Cross-border asset liquidation requires expert review' : undefined,
        confidence: 'medium' as const,
        sourceRule: 'US-India DTAA Article 13',
      })),
      dtaa: {
        applicable: country === 'USA',
        treatyCountry: country,
        keyBenefits:
          country === 'USA'
            ? [
                'Relief from double taxation on income',
                'Reduced withholding on dividends (15% vs 25%)',
                'Pension taxation clarity',
              ]
            : [],
        relevantArticles:
          country === 'USA'
            ? ['Article 4 - Residency', 'Article 13 - Capital Gains', 'Article 20 - Pensions']
            : [],
      },
      usSpecific:
        country === 'USA'
          ? {
              exitTaxApplicable: false,
              exitTaxReason: 'Exit tax applies only to US citizens/long-term residents renouncing',
              fbarRequired: true,
              fatcaRequired: true,
              estimatedFinalUSReturn: `April ${returnYear + 1}`,
            }
          : undefined,
      estimatedTotalTaxSavings_INR: 0,
      savingsConfidence: 'medium',
    },

    // Work Scenario
    workScenario: {
      recommendedPath: 'return_as_planned',
      currentPlan: {
        returnYear,
        returnMonth: 4, // April - start of Indian FY
      },
      alternativeScenarios: [
        {
          name: 'Delay by 1 year',
          returnDate: `April ${returnYear + 1}`,
          fireImpact: '+12 months additional savings',
          taxImpact: 'Additional year of US tax optimization',
        },
        {
          name: 'Accelerate by 6 months',
          returnDate: `October ${returnYear - 1}`,
          fireImpact: 'Reduced corpus by ~6 months savings',
          taxImpact: 'Earlier start to RNOR benefits',
        },
      ],
    },

    // Priority Actions
    priorityActions,

    // Constraints
    constraints: {
      targetCity: location.primaryCityId,
      targetCityTier: 1, // Default, would be from city data
      monthlyCostEstimate_INR: fireResults?.monthlyExpensesAtFIRE || 100000,
      housingPreference:
        location.housingOwnership === 'owned'
          ? 'buy'
          : location.housingOwnership === 'rental'
          ? 'rent'
          : 'undecided',
      healthcareNeeds: 'standard',
      childEducationYears: profile.children.length > 0 ? 15 : undefined,
    },

    // Applicable Rules
    applicableRules: {
      rnorRules: {
        summary:
          'Resident but Not Ordinarily Resident (RNOR) status provides tax benefits for returning NRIs for 2-3 years.',
        eligibilityCriteria: [
          'Been NRI in 9 out of 10 preceding years, OR',
          'Stayed in India for 729 days or less in preceding 7 years',
        ],
        taxBenefits: [
          'Foreign income not taxable in India',
          'Only India-sourced income is taxable',
          'Time to restructure investments tax-efficiently',
        ],
        sourceUrl: 'https://incometaxindia.gov.in',
      },
      dtaaRules:
        country === 'USA'
          ? {
              treatyName: 'India-USA Double Taxation Avoidance Agreement',
              keyArticles: [
                {
                  article: 'Article 4',
                  summary: 'Defines tax residency and tie-breaker rules',
                },
                {
                  article: 'Article 13',
                  summary: 'Capital gains taxation rules for cross-border assets',
                },
              ],
            }
          : undefined,
      countrySpecificRules:
        country === 'USA'
          ? [
              {
                country: 'USA',
                ruleName: 'FBAR Filing',
                summary:
                  'US persons must file FBAR if aggregate foreign accounts exceed $10,000',
                applicability: 'Required until you cease to be US tax resident',
              },
            ]
          : [],
    },

    // Data Quality
    dataQuality: {
      missingFields,
      assumptions: [
        {
          category: 'Inflation',
          assumption: `General inflation: ${assumptions.generalInflation}%`,
          impact: 'Affects long-term projections',
        },
        {
          category: 'Returns',
          assumption: `Pre-retirement return: ${assumptions.preRetirementReturn}%`,
          impact: 'Affects FIRE timeline',
        },
        {
          category: 'RNOR Eligibility',
          assumption: `${state.rnorResult ? '[From calculator]' : '[Estimated]'} ${rnorCalculation.explanation}. ${rnorCalculation.recommendation}`,
          impact: rnorCalculation.eligible ? 'Enables tax-free foreign income window' : 'No RNOR benefits available',
        },
      ],
      overallConfidence: confidence,
      confidenceExplanation:
        confidence === 'high'
          ? 'All required data provided. Projections are reliable.'
          : confidence === 'medium'
          ? 'Some assumptions made. Review flagged items.'
          : 'Significant data gaps. Results are preliminary estimates.',
    },

    // Pre-computed journey data from Rule Engine
    // IMPORTANT: LLM must use these exact values - no recalculation allowed
    preComputedJourneyData,
  };

  return factPack;
}

/**
 * Build FactPack with explicit FIRE results (for cases where results come separately)
 */
export function buildFactPackWithResults(
  state: CalculatorState,
  results: FIREResults
): FactPack {
  return buildFactPack({
    ...state,
    fireResults: results,
  });
}
